import { useEffect, useRef, useImperativeHandle, forwardRef } from 'react'
import classNames from 'classnames'
import styles from './ProductVideo.module.css'

declare global {
  interface Window {
    onYouTubeIframeAPIReady?: () => void
    YT: any
  }
}
interface ProductVideoProps {
  productSlideVideo: string
  className: string
}

export const ProductVideo = forwardRef((props: ProductVideoProps, ref) => {
  const { productSlideVideo } = props
  const containerRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<any>(null)

  const extractVideoId = (url: string) => {
    const match = url.match(/(?:\/embed\/|v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/)
    return match ? match[1] : ''
  }

  useEffect(() => {
    const createPlayer = () => {
      playerRef.current = new window.YT.Player('product-video', {
        videoId: extractVideoId(productSlideVideo),
        playerVars: {
          enablejsapi: 1,
          modestbranding: 1,
          rel: 0,
          playsinline: 0,
        },
        events: {
          onReady: () => {
            const iframe = document
              .getElementById('product-video')
              ?.querySelector('iframe')
            if (iframe) {
              iframe.setAttribute('allowFullScreen', 'allowFullScreen')
              iframe.setAttribute('style', 'pointer-events: none;')
              iframe.setAttribute(
                'allow',
                '"accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen'
              )
            }
          },
        },
      })
    }

    if (!window.YT || !window.YT.Player) {
      window.onYouTubeIframeAPIReady = createPlayer
      const tag = document.createElement('script')
      tag.src = 'https://www.youtube.com/iframe_api'
      const firstScriptTag = document.getElementsByTagName('script')[0]
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)
    } else {
      createPlayer()
    }

    return () => {
      if (playerRef.current?.destroy) {
        playerRef.current.destroy()
      }
    }
  }, [productSlideVideo])

  useImperativeHandle(ref, () => ({
    playVideo: () => {
      if (
        playerRef.current &&
        typeof playerRef.current.playVideo === 'function'
      ) {
        playerRef.current.playVideo()
      } else {
        console.warn(
          'Player not ready or playVideo is not a function',
          playerRef.current
        )
      }
    },
    pauseVideo: () => {
      if (
        playerRef.current &&
        typeof playerRef.current.pauseVideo === 'function'
      ) {
        playerRef.current.pauseVideo()
      }
    },
  }))

  return (
    <div className='relative h-full w-full'>
      <div
        ref={containerRef}
        id='product-video'
        className={classNames(
          props.className,
          styles.productVideo,
          'cursor-zoom-in'
        )}
      />
    </div>
  )
})
